import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLibraryItem, useLibraryChildren } from '@/lib/queries.ts';
import { pushKeyLayer, type NavKey } from '@/lib/keys.ts';
import { useBackdrop } from '@/stores/backdrop.ts';
import { Focusable } from '@/ui/Focusable.tsx';
import { api } from '@/lib/api.ts';
import { isTv } from '@/lib/native.ts';
import type { LibraryItemDetail } from '@shared/types.ts';
import { Poster } from './Poster.tsx';
import { useItemMenu } from '@/stores/itemMenu.ts';
import { ItemMenu } from './ItemMenu.tsx';
import { usePlayItem } from './usePlayItem.ts';
import { Strip } from '@/ui/Strip.tsx';

/**
 * A series or a season: what it is, and what is under it.
 *
 * The play button comes FIRST and is focused, because the commonest reason to
 * be on this screen is to carry on — the episode list is for the times you
 * want a specific one. Putting the list first would be arranging the screen
 * around the rarer intent.
 */
/** "1h 47m" — how somebody would say it, not "107 min". */
function formatRuntime(ms: number): string {
  const total = Math.round(ms / 60000);
  const h = Math.floor(total / 60);
  const m = total % 60;
  return h ? `${h}h ${m}m` : `${m}m`;
}

export function ItemPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: item, isLoading } = useLibraryItem(id);
  const { data: children } = useLibraryChildren(id);
  const { play, open } = usePlayItem();
  const [menuOpen, setMenuOpen] = useState(false);

  /*
   * This screen draws the same image as its own hero, at a treatment built
   * for it. Two of them at once is muddy rather than twice as good, so the
   * ambient one stands down while this is open and comes back on the way out.
   */
  useEffect(() => {
    const { suppress } = useBackdrop.getState();
    suppress(true);
    return () => suppress(false);
  }, []);

  useEffect(() => pushKeyLayer((k: NavKey) => {
    if (k !== 'back') return false;
    navigate(-1);
    return true;
  }), [navigate]);

  if (isLoading || !item) return <div className="muted center">Loading…</div>;

  const kids = children ?? [];
  // Seasons are 2:3 posters; episodes are 16:9 stills. Drawing both in the
  // same box would crop one of them badly.
  const wide = kids.length > 0 && kids[0].type === 'episode';

  return (
    <>
      {item.images.backdrop && (
        <div className="libe-backdrop" style={{ backgroundImage: `url(${item.images.backdrop})` }} />
      )}

      {/*
        POSTER BESIDE THE TEXT, over a backdrop that reads as a photograph
        rather than as a tint.
        
        This was a heading, a line of grey metadata and a button on a flat
        surface — correct, and indistinguishable from a settings page. The
        thing people recognise a film by is its artwork, and the detail screen
        is the one place there is room to show it properly.
      */}
      <div className="libe-detail">
        {item.images.primary && (
          <img src={item.images.primary} alt="" className="libe-detail-poster" />
        )}

        <div className="libe-detail-text">
          <h2 className="libe-heading">{item.title}</h2>

          <div className="libe-meta">
            {[
              item.year,
              item.contentRating,
              kids.length ? `${kids.length} ${wide ? 'episodes' : 'seasons'}` : null,
              /* Runtime in the form people say it, not milliseconds rounded
                 to something nobody uses. */
              item.runtimeMs ? formatRuntime(item.runtimeMs) : null,
              item.rating ? `★ ${item.rating}` : null,
            ].filter(Boolean).map((bit, i) => (
              <span key={i} className="libe-meta-bit">{bit}</span>
            ))}
            {item.genres.map((g) => (
              <span key={g} className="libe-genre">{g}</span>
            ))}
          </div>

          {item.tagline && <p className="libe-tagline">{item.tagline}</p>}
          {item.overview && <p className="libe-overview">{item.overview}</p>}

          {/* Directed by, and the studio. One line, because on a detail
              screen these are context rather than content. */}
          {(item.directors.length > 0 || item.studio) && (
            <div className="libe-credits muted">
              {item.directors.length > 0 && (
                <span>
                  {item.directors.length > 1 ? 'Directors' : 'Director'}{' '}
                  <span className="libe-credit-name">{item.directors.slice(0, 2).join(', ')}</span>
                </span>
              )}
              {item.studio && <span className="libe-credit-name">{item.studio}</span>}
            </div>
          )}

          <div className="libe-actions">
            <Focusable id="libe-play" autoFocus className="btn primary libe-play" onSelect={() => play(item)}>
              {item.userData.positionMs > 0 ? 'Resume' : 'Play'}
            </Focusable>
            <DownloadButton item={item} />
            {/* The same menu the MENU key opens on a poster. A detail page is
                where somebody with a mouse ends up, and they have no MENU key
                — so the options need a visible way in. */}
            <Focusable
              id="libe-options"
              className="btn libe-download"
              onSelect={() => setMenuOpen(true)}
            >
              Options
            </Focusable>
          </div>
        </div>
      </div>

      {menuOpen && item && <ItemMenu item={item} onClose={() => setMenuOpen(false)} />}

      {/*
        WHO IS IN IT — the other question somebody has on this screen, and the
        one the app could not answer. Plex has had the cast in the metadata
        response all along; it was being thrown away in the mapping.
      */}
      {item.cast.length > 0 && (
        <section className="libe-row libe-cast">
          <h2 className="libe-title">Cast</h2>
          <Strip>
            {item.cast.map((p) => (
              <div key={`${p.name}-${p.role ?? ''}`} className="cast-card">
                <div className="cast-face">
                  {p.thumb
                    ? <img src={p.thumb} alt="" loading="lazy" />
                    : <span className="cast-initial">{p.name.slice(0, 1)}</span>}
                </div>
                <div className="cast-name">{p.name}</div>
                {p.role && <div className="cast-role muted">{p.role}</div>}
              </div>
            ))}
          </Strip>
        </section>
      )}

      {kids.length > 0 && (
        <div className="libe-grid">
          {kids.map((k) => (
            <Poster
              key={k.id}
              item={k}
              onSelect={open}
              onOptions={(it) => useItemMenu.getState().open(it.id)}
              wide={wide}
            />
          ))}
        </div>
      )}
    </>
  );
}

/**
 * Take it with you.
 *
 * For watching in a car, on a plane, anywhere the house is not — which is a
 * thing the app this replaces does and this one did not.
 *
 * NOT ON A TELEVISION. A Fire TV has nowhere to put a file and no reason to
 * want one; the button would be a permanent reminder of something you cannot
 * do from the sofa.
 *
 * It hands over the ORIGINAL FILE, untouched. There is no transcode-to-
 * download path: the library is requested at 720p and 1080p, so the file on
 * disk is already the one worth having, and re-encoding it to make a copy
 * would cost an hour of CPU to produce something worse.
 *
 * A file the browser could not play — one that needs transcoding to watch
 * here — can still be downloaded, and often should be: the phone it lands on
 * has a better decoder than the browser does. But a transcode has no single
 * file behind it, only a playlist, so those say so rather than offering a
 * link that cannot work.
 */
function DownloadButton({ item }: { item: LibraryItemDetail }) {
  const [state, setState] = useState<'idle' | 'working' | 'unavailable'>('idle');
  if (isTv()) return null;

  return (
    <Focusable
      id="libe-download"
      className="btn libe-download"
      onSelect={() => {
        if (state === 'working') return;
        setState('working');
        void api.downloadUrl(item.id)
          .then((url) => {
            if (!url) { setState('unavailable'); return; }
            setState('idle');
            /*
             * A plain anchor click, not `location.href`. Navigating away from
             * the app to a download tears down the page — and with it
             * whatever is playing, which is the opposite of what somebody
             * queuing a download for later wants.
             */
            const a = document.createElement('a');
            a.href = url;
            a.download = '';
            document.body.appendChild(a);
            a.click();
            a.remove();
          })
          .catch(() => setState('unavailable'));
      }}
    >
      {state === 'working' ? 'Preparing…'
        : state === 'unavailable' ? 'Not downloadable'
          : 'Download'}
    </Focusable>
  );
}
